import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart,
  ShoppingCart,
  Trash2,
  ArrowLeft,
  ExternalLink,
  Sparkles,
  PackageCheck,
  Store
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  fetchProducts,
  selectAllProducts
} from '../store/slices/productsSlice';
import {
  selectWishlistItems,
  removeFromWishlist,
  clearWishlist
} from '../store/slices/wishlistSlice';
import { addToCart } from '../store/slices/cartSlice';
import { database } from '../services/firebase';
import { ref, onValue } from 'firebase/database';
import Button from '../components/common/Button';
import ValiditySelectionModal from '../components/common/ValiditySelectionModal';
import toastManager from '../utils/toastManager';
import { formatPrice } from '../utils/helpers';
import {
  isExternalProduct,
  isFreeScriptProduct,
  normalizeCategory,
  isPurchasableProduct,
  resolveFreeScriptDownloadLink
} from '../utils/productRules';
import { getAuthRedirectPath } from '../utils/authRedirect';

const fallbackImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300' fill='none'%3E%3Crect width='300' height='300' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%239ca3af' font-size='14' font-family='sans-serif'%3ENo Image%3C/text%3E%3C/svg%3E";
const heroShellClass = 'relative overflow-hidden rounded-3xl border border-primary-100 bg-gradient-to-br from-white via-primary-50 to-amber-50 text-gray-950 shadow-sm dark:border-white/10 dark:bg-gray-950 dark:bg-none dark:text-white dark:shadow-xl';
const heroGlowClass = 'absolute inset-0 bg-gradient-to-br from-primary-500/10 via-accent-500/10 to-amber-400/15 dark:from-primary-500/30 dark:via-accent-500/25 dark:to-amber-400/10';
const heroChipClass = 'inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-white/80 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-primary-700 shadow-sm dark:border-white/10 dark:bg-white/[0.08] dark:text-primary-100 dark:shadow-none';
const heroIconPanelClass = 'border border-primary-100 bg-white/70 text-primary-700 shadow-sm dark:border-white/10 dark:bg-white/[0.08] dark:text-white dark:shadow-none';

const Wishlist = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settings?.settings || {});
  const allProducts = useSelector(selectAllProducts);
  const wishlistItems = useSelector(selectWishlistItems);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showValidityModal, setShowValidityModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [productAccessMap, setProductAccessMap] = useState({});

  React.useEffect(() => {
    const t = setTimeout(() => setIsLoading(false), 400);
    return () => clearTimeout(t);
  }, []);

  React.useEffect(() => {
    if (!Array.isArray(allProducts) || allProducts.length === 0) {
      dispatch(fetchProducts());
    }
  }, [allProducts, dispatch]);

  React.useEffect(() => {
    if (!user?.uid) {
      setProductAccessMap({});
      return undefined;
    }

    const accessRef = ref(database, `Users/${user.uid}/ProductAccess`);
    return onValue(
      accessRef,
      (snapshot) => {
        setProductAccessMap(snapshot.exists() ? (snapshot.val() || {}) : {});
      },
      () => setProductAccessMap({})
    );
  }, [user?.uid]);

  const productIndex = useMemo(() => {
    const index = {};
    (allProducts || []).forEach((product) => {
      if (product?.id) index[String(product.id)] = product;
    });
    return index;
  }, [allProducts]);

  const isAccessActive = (accessRecord = {}) => {
    if (!accessRecord || typeof accessRecord !== 'object') return false;
    const status = String(accessRecord.status || 'active').toLowerCase();
    if (status === 'revoked' || status === 'inactive' || status === 'expired') return false;
    if (accessRecord.expiresAt === null || accessRecord.expiresAt === 'lifetime') return true;
    if (!accessRecord.expiresAt && String(accessRecord.validity || '').toLowerCase() === 'lifetime') return true;
    const expiresAt = accessRecord.expiresAt ? new Date(accessRecord.expiresAt).getTime() : 0;
    return !expiresAt || expiresAt > Date.now();
  };

  const getBundleProductIds = (bundleProduct = {}) => {
    if (Array.isArray(bundleProduct.Products)) return bundleProduct.Products.map(String);
    if (Array.isArray(bundleProduct.bundleItems)) return bundleProduct.bundleItems.map(String);
    return [];
  };

  const isOwnedWishlistProduct = (item) => {
    if (!item?.id || !isAuthenticated) return false;
    const itemId = String(item.id);

    if (isAccessActive(productAccessMap?.[itemId])) {
      return true;
    }

    return Object.entries(productAccessMap || {}).some(([accessProductId, accessRecord]) => {
      if (!isAccessActive(accessRecord)) return false;
      const accessProduct = productIndex[String(accessProductId)];
      if (normalizeCategory(accessProduct?.category) !== 'bundle') return false;
      return getBundleProductIds(accessProduct).includes(itemId);
    });
  };

  const preventOwnedWishlistPurchase = (item) => {
    if (!isOwnedWishlistProduct(item)) return false;
    toastManager.info(t('wishlist.already_owned', 'You already own this product. Use My Scripts to extend or manage access.'));
    dispatch(removeFromWishlist(item.id));
    return true;
  };

  if (!isAuthenticated) {
    return <Navigate to={getAuthRedirectPath()} replace />;
  }

  const handleRemoveFromWishlist = (productId) => {
    dispatch(removeFromWishlist(productId));
    toastManager.success(t('product.removed_from_wishlist'));
  };

  const handleAddToCart = (item) => {
    if (preventOwnedWishlistPurchase(item)) {
      return;
    }

    if (!isPurchasableProduct(item)) {
      toastManager.error('This product is not available for purchase');
      return;
    }

    const priceMap = item.pricingRaw || item.Price || {};
    const validityOptions = Object.entries(priceMap)
      .filter(([key, value]) => typeof value === 'number' && value > 0);

    if (validityOptions.length === 0 && typeof item.price === 'number' && item.price > 0) {
      const productForCart = {
        id: item.id,
        name: item.name,
        price: item.price,
        image: item.image,
        category: item.category
      };

      dispatch(addToCart({
        product: productForCart,
        validity: null
      }));

      dispatch(removeFromWishlist(item.id));
      toastManager.success(t('wishlist.item_moved', { name: item.name }));
      return;
    }

    if (validityOptions.length > 0) {
      setSelectedProduct(item);
      setShowValidityModal(true);
      return;
    }

    toastManager.error(t('common.not_available'));
  };

  const handleValiditySelection = (validity, price) => {
    if (!selectedProduct) return;
    if (preventOwnedWishlistPurchase(selectedProduct)) {
      setSelectedProduct(null);
      setShowValidityModal(false);
      return;
    }

    if (!isPurchasableProduct(selectedProduct)) {
      toastManager.error('This product is not available for purchase');
      return;
    }

    const productForCart = {
      id: selectedProduct.id,
      name: selectedProduct.name,
      price: price,
      image: selectedProduct.image,
      category: selectedProduct.category
    };

    dispatch(addToCart({
      product: productForCart,
      validity: validity
    }));

    dispatch(removeFromWishlist(selectedProduct.id));
    toastManager.success(t('wishlist.item_moved', { name: selectedProduct.name }));

    setSelectedProduct(null);
    setShowValidityModal(false);
  };

  const handleOpenExternal = (item) => {
    if (item.ExternalLink || item.externalLink) {
      window.open(item.ExternalLink || item.externalLink, '_blank', 'noopener,noreferrer');
    }
  };

  const isFreeProduct = (item) => isFreeScriptProduct(item);

  const handleClearWishlist = () => {
    dispatch(clearWishlist());
    toastManager.success(t('wishlist.cleared'));
  };

  const handleStartShopping = () => {
    navigate('/products');
  };

  const handleViewProduct = (productId) => {
    navigate(`/product/${productId}`);
  };

  const formatPriceDisplay = (item) => {
    const priceMap = item.pricingRaw || item.Price || {};
    const validPrices = Object.entries(priceMap)
      .filter(([key, value]) => typeof value === 'number' && value > 0);

    if (validPrices.length > 0) {
      const lowestPrice = Math.min(...validPrices.map(([key, value]) => value));
      const lowestPriceEntry = validPrices.find(([key, value]) => value === lowestPrice);
      return <span className="notranslate" translate="no">{formatPrice(lowestPrice)}/{lowestPriceEntry[0]}</span>;
    }

    if (typeof item.price === 'number' && item.price > 0) {
      return <span className="notranslate" translate="no">{formatPrice(item.price)}</span>;
    }

    return t('wishlist.contact_pricing');
  };

  const isProductAvailable = (item) => {
    if (!isPurchasableProduct(item)) return false;
    const priceMap = item.pricingRaw || item.Price || {};
    const validPrices = Object.entries(priceMap)
      .filter(([key, value]) => typeof value === 'number' && value > 0);

    return validPrices.length > 0 || (typeof item.price === 'number' && item.price > 0);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50/80 py-4 dark:bg-gray-950 sm:py-6">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="mb-4 h-28 animate-pulse rounded-3xl border border-primary-100 bg-primary-50 dark:border-gray-800 dark:bg-gray-900" />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="rounded-3xl border border-gray-100 bg-white p-2 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                <div className="aspect-square animate-pulse rounded-2xl bg-gray-100 dark:bg-gray-800" />
                <div className="space-y-2 p-2">
                  <div className="h-4 animate-pulse rounded bg-gray-100 dark:bg-gray-800" />
                  <div className="h-4 w-2/3 animate-pulse rounded bg-gray-100 dark:bg-gray-800" />
                  <div className="h-9 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (wishlistItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50/80 py-4 dark:bg-gray-950 sm:py-6">
        <div className="container mx-auto max-w-5xl px-4">
          <motion.section
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            className={`${heroShellClass} p-5 sm:p-6`}
          >
            <div className={heroGlowClass} />
            <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className={`mb-3 ${heroChipClass}`}>
                  <Sparkles className="h-3.5 w-3.5 text-amber-300" />
                  {t('wishlist.title', { count: 0 })}
                </div>
                <h1 className="text-2xl font-black leading-tight sm:text-3xl">
                  {t('wishlist.empty_title')}
                </h1>
                <p className="mt-2 max-w-xl text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                  {t('wishlist.empty_subtitle')}
                </p>
              </div>
              <div className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-3xl ${heroIconPanelClass}`}>
                <Heart className="h-8 w-8 text-rose-500 dark:text-rose-300" />
              </div>
            </div>
          </motion.section>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 }}
            className="mt-4 rounded-3xl border border-dashed border-gray-200 bg-white p-5 text-center shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:p-6"
          >
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-50 text-rose-500 dark:bg-rose-500/10 dark:text-rose-300">
              <Heart className="h-6 w-6" />
            </div>
            <p className="mx-auto mb-4 max-w-md text-sm text-gray-500 dark:text-gray-400">
              {t('wishlist.empty_subtitle')}
            </p>
            <Button
              onClick={handleStartShopping}
              variant="primary"
              size="md"
              className="w-full sm:w-auto"
              icon={Store}
            >
              {t('cart.start_shopping')}
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/80 py-4 dark:bg-gray-950 sm:py-6">
      <div className="container mx-auto max-w-6xl px-4">
        <motion.section
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          className={`${heroShellClass} mb-4 p-4 sm:p-5`}
        >
          <div className={heroGlowClass} />
          <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl transition hover:bg-white dark:hover:bg-white/[0.14] ${heroIconPanelClass}`}
                aria-label={t('common.back', 'Back')}
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <div className="min-w-0">
                <div className={`mb-1 ${heroChipClass}`}>
                  <Sparkles className="h-3 w-3 text-amber-300" />
                  Saved picks
                </div>
                <h1 className="truncate text-2xl font-black leading-tight sm:text-3xl">
                  {t('wishlist.title', { count: wishlistItems.length })}
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className={`hidden rounded-2xl px-3 py-2 text-left sm:block ${heroIconPanelClass}`}>
                <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  <PackageCheck className="h-3.5 w-3.5" />
                  Items
                </div>
                <p className="mt-0.5 text-lg font-black">{wishlistItems.length}</p>
              </div>
              <button
                onClick={handleClearWishlist}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-3 text-xs font-black text-red-600 transition hover:bg-red-100 dark:border-red-300/20 dark:bg-red-500/10 dark:text-red-100 dark:hover:bg-red-500/20"
              >
                <Trash2 className="h-4 w-4" />
                <span>{t('wishlist.clear_all')}</span>
              </button>
            </div>
          </div>
        </motion.section>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          <AnimatePresence>
            {wishlistItems.map((item, index) => {
              const isOwned = isOwnedWishlistProduct(item);

              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ delay: index * 0.025 }}
                  className="group cursor-pointer overflow-hidden rounded-3xl border border-gray-100 bg-white p-1.5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-md dark:border-gray-800 dark:bg-gray-900 dark:hover:border-primary-500/40"
                  onClick={() => handleViewProduct(item.id)}
                >
                  <div className="relative overflow-hidden rounded-2xl bg-gray-100 dark:bg-gray-800">
                    <img
                      src={item.image || fallbackImage}
                      alt={item.name}
                      className="aspect-square w-full object-cover transition duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-x-2 top-2 flex items-center justify-between gap-2">
                      <span className="max-w-[70%] truncate rounded-full bg-gray-950/80 px-2 py-1 text-[9px] font-black uppercase text-white backdrop-blur">
                        {isOwned ? t('common.owned', 'Owned') : item.category || t('common.product', 'Product')}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFromWishlist(item.id);
                        }}
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-red-500 shadow-lg transition hover:bg-red-50 dark:bg-gray-950 dark:hover:bg-red-500/10"
                        title={t('wishlist.remove_confirm')}
                        aria-label={t('wishlist.remove_confirm')}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>

                  <div className="flex min-h-[118px] flex-col p-2.5">
                    <h3 className="line-clamp-2 text-sm font-bold leading-snug text-gray-950 dark:text-white">
                      {item.name}
                    </h3>

                    <p className="mt-1 text-base font-black text-primary-600 dark:text-primary-300">
                      {formatPriceDisplay(item)}
                    </p>

                    <div className="mt-auto pt-3">
                      {isOwned ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full text-xs"
                          disabled
                        >
                          {t('common.owned', 'Owned')}
                        </Button>
                      ) : isExternalProduct(item) ? (
                        <Button
                          variant="primary"
                          size="sm"
                          className="w-full text-xs"
                          icon={ExternalLink}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenExternal(item);
                          }}
                        >
                          {t('product.open')}
                        </Button>
                      ) : isFreeProduct(item) ? (
                        <Button
                          variant="primary"
                          size="sm"
                          className="w-full text-xs"
                          icon={ExternalLink}
                          onClick={(e) => {
                            e.stopPropagation();
                            const link = resolveFreeScriptDownloadLink(item, settings);
                            if (!link) {
                              toastManager.error('Download link is not available yet');
                              return;
                            }
                            window.open(link, '_blank', 'noopener,noreferrer');
                          }}
                        >
                          {t('product.download_now', 'Download')}
                        </Button>
                      ) : !isPurchasableProduct(item) ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full text-xs opacity-50"
                          disabled
                        >
                          {t('product.not_for_sale', 'Not for sale')}
                        </Button>
                      ) : isProductAvailable(item) ? (
                        <Button
                          variant="primary"
                          size="sm"
                          className="w-full text-xs"
                          icon={ShoppingCart}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddToCart(item);
                          }}
                        >
                          {t('product.add_to_cart')}
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full text-xs opacity-50"
                          disabled
                        >
                          {t('wishlist.not_available')}
                        </Button>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>

      <ValiditySelectionModal
        isOpen={showValidityModal}
        onClose={() => {
          setShowValidityModal(false);
          setSelectedProduct(null);
        }}
        product={selectedProduct}
        onAddToCart={handleValiditySelection}
        title={t('wishlist.billing_period')}
      />
    </div>
  );
};

export default Wishlist;
